import { useState, useEffect } from "react";
import { API } from "./api";

const EMPTY = { access_key: "", secret_key: "", bucket: "", region: "us-east-1", endpoint: "", base_url: "", enabled: false };

const HINTS = {
  idrive: { endpoint: "https://<region>.idrivee2-XX.com  (e.g. https://k8x1.va.idrivee2-XX.com)", acct: "https://www.idrive.com/e2/" },
  backblaze: { endpoint: "https://s3.us-west-004.backblazeb2.com", acct: "https://www.backblaze.com/cloud-storage" },
};

function ProviderForm({ p, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [hasSecret, setHasSecret] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    setForm({
      access_key: p.access_key || "", secret_key: "", bucket: p.bucket || "",
      region: p.region || "us-east-1", endpoint: p.endpoint || "",
      base_url: p.base_url || "", enabled: !!p.enabled,
    });
    setHasSecret(!!p.has_secret);
  }, [p]);

  const save = async () => {
    setBusy(true); setMsg(null);
    // Auto-enable when credentials are present
    const payload = { ...form, enabled: form.enabled || !!(form.access_key && form.bucket && form.endpoint) };
    try { await API.saveStorage(p.provider, payload); onSaved(); setMsg({ ok: true, text: "Saved" }); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setMsg(null);
    const payload = { ...form, enabled: true };
    try { await API.saveStorage(p.provider, payload); const r = await API.testStorage(p.provider); onSaved(); setMsg({ ok: true, text: r.message || "Connected" }); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  const hint = HINTS[p.provider] || {};
  return (
    <div className="panel" data-testid={`storage-${p.provider}`}>
      <div className="head-row">
        <h2 style={{ margin: 0 }}>☁ {p.label} {p.enabled && <span className="tag" style={{ background: "#0f5132", color: "#7ee2a8" }}>enabled</span>}</h2>
        <a className="link" href={hint.acct} target="_blank" rel="noreferrer">Get an account →</a>
      </div>
      <div className="form-grid" style={{ marginTop: 12 }}>
        <label>Access Key ID<input value={form.access_key} onChange={(e) => set("access_key", e.target.value)} data-testid={`${p.provider}-access`} /></label>
        <label>Secret Access Key
          <input type="password" value={form.secret_key} placeholder={hasSecret ? "•••••• (saved — leave blank to keep)" : ""}
            onChange={(e) => set("secret_key", e.target.value)} data-testid={`${p.provider}-secret`} autoComplete="off" />
        </label>
        <label>Bucket<input value={form.bucket} onChange={(e) => set("bucket", e.target.value)} data-testid={`${p.provider}-bucket`} /></label>
        <label>Region<input value={form.region} onChange={(e) => set("region", e.target.value)} data-testid={`${p.provider}-region`} /></label>
        <label className="wide">S3 Endpoint<input value={form.endpoint} onChange={(e) => set("endpoint", e.target.value)} placeholder={hint.endpoint} data-testid={`${p.provider}-endpoint`} /></label>
        <label className="wide">Public Base URL (optional — for permanent public file links)<input value={form.base_url} onChange={(e) => set("base_url", e.target.value)} placeholder="https://cdn.mysite.com" /></label>
        <label className="wide" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid={`${p.provider}-enabled`} style={{ width: "auto" }} />
          Enable this drive in the Media Manager
        </label>
      </div>
      <div className="actions" style={{ marginTop: 12 }}>
        <button className="btn primary" onClick={save} disabled={busy} data-testid={`${p.provider}-save`}>Save</button>
        <button className="btn ghost" onClick={test} disabled={busy} data-testid={`${p.provider}-test`}>Test Connection</button>
        <button
          className="btn ghost"
          disabled={busy || !p.configured}
          title={p.configured ? "Push credentials to live server" : "Save credentials first"}
          data-testid={`${p.provider}-push-live`}
          onClick={async () => {
            setBusy(true); setMsg(null);
            try {
              const r = await API.syncPushStorage(p.provider);
              setMsg({ ok: true, text: r.message || "Pushed to live server ✓" });
            } catch (e) { setMsg({ ok: false, text: e.message }); }
            finally { setBusy(false); }
          }}
          style={{ background: p.configured ? "#0d6efd" : undefined, color: p.configured ? "#fff" : undefined }}
        >Push to Live</button>
        {msg && <span className={msg.ok ? "msg-ok" : "msg-error"} data-testid={`${p.provider}-msg`}>{msg.ok ? "✓ " : "✕ "}{msg.text}</span>}
        {!p.enabled && p.access_key && p.bucket && (
          <span style={{ color: "#c0392b", fontSize: 12, marginLeft: 8 }}>
            ⚠ Credentials saved but drive is not enabled. Click Save to activate.
          </span>
        )}
      </div>
    </div>
  );
}

function AiRulesEditor() {
  const [rules, setRules] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.getAiRules().then((r) => setRules(r.rules || "")).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await API.saveAiRules(rules);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (_) {}
    finally { setBusy(false); }
  };

  return (
    <div className="panel ai-rules-panel" data-testid="ai-rules-panel">
      <div className="head-row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>&#9881;</span> AI Rules
        </h2>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>Agent reads these before every task</span>
      </div>
      <p style={{ fontSize: 13, color: "var(--muted)", margin: "0 0 12px", lineHeight: 1.5 }}>
        Write rules, constraints, and preferences the AI agent must follow.
        These are saved and enforced on every task — no need to repeat yourself.
      </p>
      <textarea
        data-testid="ai-rules-input"
        value={rules}
        onChange={(e) => setRules(e.target.value)}
        rows={12}
        placeholder={"Example rules:\n- Never add domain names to images unless I explicitly ask\n- Never change branding text without being told\n- Only do exactly what I ask, nothing extra\n- Always ask before touching existing images or files\n- Brand name is 'Ai Joomla' — no 'Builder', no .com domain"}
        style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 13, lineHeight: 1.6, resize: "vertical" }}
      />
      <div className="actions" style={{ marginTop: 10 }}>
        <button className="btn primary" onClick={save} disabled={busy} data-testid="ai-rules-save">
          {saved ? "✓ Saved" : "Save Rules"}
        </button>
        {saved && <span className="msg-ok">Rules saved successfully</span>}
      </div>
    </div>
  );
}

export default function Settings() {
  const [providers, setProviders] = useState([]);
  const load = () => API.storageProviders().then(setProviders).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="page-settings">
      <h1>Settings</h1>

      <AiRulesEditor />

      <h2 style={{ marginTop: 32, marginBottom: 14, fontSize: 18 }}>Cloud Storage</h2>
      <p className="hint" style={{ marginBottom: 16 }}>
        Connect your <b>iDrive e2</b> or <b>Backblaze B2</b> bucket. Once enabled, they appear as drives
        in the Media Manager — upload boxes, screenshots and install ZIPs straight into your cloud bucket.
      </p>
      <div className="settings-stack">
        {providers.map((p) => <ProviderForm key={p.provider} p={p} onSaved={load} />)}
      </div>
    </div>
  );
}
